// `/login` on the admin host — spec 15 §4 S1 and §3.1 step 4.
//
// TWO STEPS, ONE SCREEN, and the branch is the SERVER's answer, never a guess:
//   * `step: "totp_enrollment_required"` — the account has a password but no
//     TOTP. A RESTRICTED session cookie is already set, and it reaches only the
//     enrolment endpoints, so the screen sends them to /enroll (§3.1.3, M15-S03
//     "they are forced into the enrollment screen").
//   * `step: "totp"` — the account is active; the server hands back a
//     single-use `login_ticket` (5-minute TTL) which the TOTP step must present.
//
// THE LOGIN TICKET IS A CREDENTIAL and is held in component state ONLY: never
// in localStorage, never in the URL, never in a log. It dies with the screen,
// which is why an expired/consumed ticket sends the user back to step one with
// a sentence rather than silently retrying.
//
// LOCKOUT IS SURFACED HONESTLY (§3.1.4: 10 failures → 15 minutes) without ever
// revealing whether the address exists: the wrong-credentials sentence is the
// same either way, and 423 is a state of "this account", not of "this address"
// — an attacker who can trip it has already had ten guesses at that address.
//
// THE TICKET DIES ON THE FIRST WRONG CODE, AND THIS SCREEN SAYS SO. Read
// `StaffAuthService.totpStep`: `consumeLoginTicket` DELETES the ticket and that
// delete COMMITS even when the code is rejected (the throw is deliberately
// after commit, so the failure counter commits with it). The ticket is
// therefore single-attempt — not single-use-on-success — and the old screen
// left the form standing after a miss, inviting a second attempt that the
// server had already made impossible and then answering the CORRECT code with
// "that sign-in step timed out". So any answer that proves the service ran
// (401/423) returns the user to step one WITH THE REASON, and the two remaining
// cases are separated because neither burns anything:
//
//   * a 400/422 is the CONTRACT refusing the shape before the service is
//     reached — the ticket is untouched, so the screen stays and says what the
//     field wants. The submit button gates on the same shapes, so this is the
//     belt to the browser's braces rather than the normal path;
//   * a transport failure (no PlatformError) never reached the server at all —
//     the screen stays and offers the retry, because retrying is correct.
//
// AND THE MESSAGE FOLLOWS THE MODE. Someone in recovery-code mode has already
// lost their phone; telling them "codes change every 30 seconds — try the
// current one" is advice they cannot act on, addressed to a device they do not
// have. Recovery failures get their own sentence.

import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Banner } from "../../../components/toast";
import { Button } from "../../../components/button";
import { AdminAuthCard } from "../admin-shell";
import { formatWait } from "../lib/format-wait";
import { platformLogin, platformTotpStep, PlatformError } from "../lib/platform-api";
import { takeAdminFlash } from "../lib/flash";

type Step = { kind: "password" } | { kind: "totp"; ticket: string };

/** Why the screen threw the user back to step one — each has its own sentence. */
type Restart = "expired" | "rejectedCode" | "rejectedRecovery" | "locked" | "brokenTicket";

/** The 6 digits the contract accepts (`PlatformTotpRequest.code`). */
const TOTP_SHAPE = /^\d{6}$/u;
/** A recovery code as minted: two groups of five Crockford-ish characters. */
const RECOVERY_SHAPE = /^[0-9A-HJKMNP-TV-Z]{5}-[0-9A-HJKMNP-TV-Z]{5}$/u;

export function AdminLoginScreen() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  // Read the one-shot hand-off from accept-invite exactly once per mount.
  const [flash] = useState(() => takeAdminFlash());
  const [step, setStep] = useState<Step>({ kind: "password" });
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);
  const [restart, setRestart] = useState<Restart | undefined>(undefined);

  // Uppercased and trimmed, because a recovery code is minted uppercase and the
  // phone keyboard that types it is not. The TOTP branch is unaffected (digits).
  const entered = code.trim().toUpperCase();
  const enteredIsWellFormed = useRecovery ? RECOVERY_SHAPE.test(entered) : TOTP_SHAPE.test(entered);

  const login = useMutation({
    mutationFn: () => platformLogin({ email, password }),
    onSuccess: (result) => {
      setPassword("");
      if (result.step === "totp_enrollment_required") {
        void navigate({ to: "/enroll" });
        return;
      }
      // `login_ticket` is optional in the schema because the enrolment branch
      // above carries none. On THIS branch its absence is a broken contract,
      // not a state to paper over with an empty string: an empty ticket would
      // be posted, refused, and read to the user as a rejected CODE — blaming
      // them for the server's answer. Say the step could not be started.
      const ticket = result.login_ticket;
      if (ticket === undefined || ticket === "") {
        setRestart("brokenTicket");
        return;
      }
      setStep({ kind: "totp", ticket });
    },
  });

  /** Back to email + password, with the reason on the glass. */
  const restartWith = (reason: Restart): void => {
    setStep({ kind: "password" });
    setCode("");
    setRestart(reason);
  };

  const verify = useMutation({
    mutationFn: async () => {
      if (step.kind !== "totp") throw new Error("no login ticket");
      await platformTotpStep({
        login_ticket: step.ticket,
        ...(useRecovery ? { recovery_code: entered } : { code: entered }),
      });
    },
    onSuccess: () => {
      setCode("");
      void navigate({ to: "/console" });
    },
    onError: (error: unknown) => {
      // Transport failure — the request may never have reached the server, so
      // the ticket is presumed alive and the screen stays with a retry.
      if (!(error instanceof PlatformError)) return;
      // The contract refused the shape; the service never ran and the ticket
      // is untouched. Stay put and say what the field wants.
      if (error.status === 400 || error.status === 422) return;
      // Everything else means `consumeLoginTicket` ran — and it commits its
      // DELETE whatever the verdict is, so this ticket is gone.
      if (error.status === 423) {
        restartWith("locked");
        return;
      }
      if (error.code === "LOGIN_TICKET_INVALID") {
        restartWith("expired");
        return;
      }
      restartWith(useRecovery ? "rejectedRecovery" : "rejectedCode");
    },
  });

  const passwordError = ((): string | undefined => {
    const error = login.error;
    if (error === null) return undefined;
    if (!(error instanceof PlatformError)) return t("admin.login.error");
    if (error.status === 423) return t("admin.login.locked");
    if (error.status === 401) return t("admin.login.failed");
    // D-494: too many password steps from this client or for this address.
    // The wait is the server's Retry-After, said the locale's own way.
    if (error.status === 429) {
      const seconds = error.retryAfterSeconds;
      if (seconds === undefined) return t("admin.login.throttledLater");
      return t("admin.login.throttled", { wait: formatWait(seconds, i18n.language) });
    }
    return t("admin.login.error");
  })();

  const RESTART_KEY: Record<Restart, string> = {
    expired: "admin.login.expired",
    rejectedCode: "admin.login.totpFailed",
    rejectedRecovery: "admin.login.recoveryFailed",
    locked: "admin.login.locked",
    brokenTicket: "admin.login.error",
  };

  // The ONLY errors that survive on the TOTP step are the two that leave the
  // ticket alive; every other verdict has already moved the user to step one.
  const totpError = ((): string | undefined => {
    const error = verify.error;
    if (error === null) return undefined;
    if (!(error instanceof PlatformError)) return t("admin.login.error");
    if (error.status === 400 || error.status === 422) {
      return useRecovery ? t("admin.login.recoveryFormat") : t("admin.login.codeFormat");
    }
    return undefined; // shown on step one
  })();

  if (step.kind === "totp") {
    return (
      <AdminAuthCard
        title={t("admin.login.totpTitle")}
        // The lead FOLLOWS the mode. It used to say "enter the 6-digit code
        // from your authenticator app" while the field below it was labelled
        // "Recovery code" — the screen contradicted itself, on the one screen
        // reached by someone who has just lost their phone and is least able to
        // absorb a contradiction.
        lead={useRecovery ? t("admin.login.recoveryLead") : t("admin.login.totpLead")}
        data-testid="admin-login"
      >
        {totpError !== undefined ? (
          <Banner kind="bad" data-testid="admin-totp-error">
            {totpError}
          </Banner>
        ) : null}
        <form
          className="admin-form"
          data-testid="admin-totp-form"
          onSubmit={(e) => {
            e.preventDefault();
            // The ticket survives exactly ONE submission, so the browser must
            // not spend it on something it can already see is malformed.
            if (!enteredIsWellFormed) return;
            verify.mutate();
          }}
        >
          <div className="ds-field">
            <label htmlFor="admin-totp">
              {useRecovery ? t("admin.login.recoveryCode") : t("admin.login.code")}
            </label>
            <input
              id="admin-totp"
              className="ds-input admin-code"
              type="text"
              inputMode={useRecovery ? "text" : "numeric"}
              autoComplete="one-time-code"
              autoFocus
              spellCheck={false}
              autoCapitalize="characters"
              value={code}
              data-testid="admin-totp-code"
              onChange={(e) => {
                setCode(e.target.value);
              }}
              required
            />
            <p className="admin-hint">
              {useRecovery ? t("admin.login.recoveryHint") : t("admin.login.codeHint")}
            </p>
          </div>
          <div className="admin-actions">
            <Button
              type="submit"
              variant="primary"
              busy={verify.isPending}
              disabled={!enteredIsWellFormed}
              data-testid="admin-totp-submit"
            >
              {verify.isPending ? t("admin.login.verifying") : t("admin.login.verify")}
            </Button>
            <Button
              size="sm"
              onClick={() => {
                setUseRecovery((v) => !v);
                setCode("");
              }}
              data-testid="admin-recovery-toggle"
            >
              {useRecovery ? t("admin.login.useCode") : t("admin.login.useRecovery")}
            </Button>
          </div>
        </form>
      </AdminAuthCard>
    );
  }

  return (
    <AdminAuthCard
      title={t("admin.login.title")}
      lead={t("admin.login.lead")}
      data-testid="admin-login"
    >
      {flash === "password_set" ? (
        <Banner kind="ok" data-testid="admin-password-set">
          {t("admin.login.passwordSet")}
        </Banner>
      ) : null}
      {flash === "password_reset" ? (
        // The reset screen set a new password (D-587). It says the second
        // factor still applies, because the link proved only the mailbox.
        <Banner kind="ok" data-testid="admin-password-reset-done">
          {t("admin.login.passwordReset")}
        </Banner>
      ) : null}
      {flash === "session_expired" ? (
        // The console or the enrolment screen sent them here because a write
        // came back 401 with no TOTP verdict — the session died mid-action.
        // Neutral, not bad: nothing they did was refused, and the sentence must
        // say the SESSION failed so they do not re-litigate a code that was
        // never judged.
        <Banner kind="neutral" data-testid="admin-session-expired">
          {t("admin.login.sessionExpired")}
        </Banner>
      ) : null}
      {restart !== undefined ? (
        <Banner kind="bad" data-testid="admin-ticket-expired">
          {t(RESTART_KEY[restart])}
        </Banner>
      ) : null}
      {passwordError !== undefined ? (
        <Banner kind="bad" data-testid="admin-login-error">
          {passwordError}
        </Banner>
      ) : null}

      <form
        className="admin-form"
        data-testid="admin-login-form"
        onSubmit={(e) => {
          e.preventDefault();
          setRestart(undefined);
          login.mutate();
        }}
      >
        <div className="ds-field">
          <label htmlFor="admin-email">{t("admin.login.email")}</label>
          <input
            id="admin-email"
            className="ds-input"
            type="email"
            autoComplete="username"
            spellCheck={false}
            autoCapitalize="off"
            value={email}
            data-testid="admin-email"
            onChange={(e) => {
              setEmail(e.target.value);
            }}
            required
          />
        </div>
        <div className="ds-field">
          <label htmlFor="admin-login-password">{t("admin.login.password")}</label>
          <input
            id="admin-login-password"
            className="ds-input"
            type="password"
            autoComplete="current-password"
            value={password}
            data-testid="admin-login-password"
            onChange={(e) => {
              setPassword(e.target.value);
            }}
            required
          />
        </div>
        <div className="admin-actions">
          <Button
            type="submit"
            variant="primary"
            busy={login.isPending}
            data-testid="admin-login-submit"
          >
            {login.isPending ? t("admin.login.submitting") : t("admin.login.submit")}
          </Button>
        </div>
        {/* §3.1 step 7 (D-587): the one way back for a forgotten password. */}
        <div className="auth-links">
          <Link to="/forgot-password" className="ds-lnk" data-testid="admin-forgot-link">
            {t("admin.login.forgot")}
          </Link>
        </div>
      </form>
    </AdminAuthCard>
  );
}
